import { Box, Link, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useCommonApi } from '../api';
import { surfaceSx } from '../styles/tokens';
import { PageLayout } from './common';
import { useAuth } from './hooks';
import { Income } from './types/income';

interface PhmPriceCellProps {
  row: Income;
  year: string;
  savedPrice?: number;
  savedLiters?: number;
  onSave: (year: string, month: string, price: number, liters?: number) => void;
}

/*const PhmPriceCell = ({
  row,
  year,
  savedPrice,
  savedLiters,
  onSave,
}: PhmPriceCellProps) => {
  const paddedMonth = String(row.month).padStart(2, "0");
  const [value, setValue] = useState(
    savedPrice != null ? String(savedPrice) : "",
  );
  const [litersValue, setLitersValue] = useState(
    savedLiters != null ? String(savedLiters) : "",
  );

  useEffect(() => {
    if (savedPrice != null) setValue(String(savedPrice));
    if (savedLiters != null) {
      setLitersValue(String(savedLiters));
    } else {
      setLitersValue("");
    }
  }, [savedPrice, savedLiters]);

  const handleSave = () => {
    const parsedPrice = Number.parseFloat(value.replace(",", "."));
    const parsedLiters = Number.parseFloat(litersValue.replace(",", "."));
    if (Number.isNaN(parsedPrice)) return;
    onSave(
      year,
      paddedMonth,
      parsedPrice,
      Number.isNaN(parsedLiters) ? undefined : parsedLiters,
    );
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: "5px" }}>
      <TextField
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
        }}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">Kč</InputAdornment>,
          },
          htmlInput: { inputMode: "decimal" },
        }}
        sx={{ width: 130 }}
      />
      <TextField
        size="small"
        value={litersValue}
        onChange={(e) => setLitersValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
        }}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">l</InputAdornment>,
          },
          htmlInput: { inputMode: "decimal" },
        }}
        sx={{ width: 130 }}
      />
      <Button
        size="small"
        variant="contained"
        color="success"
        sx={{
          py: 1,
        }}
        onClick={handleSave}
        disabled={
          value === "" ||
          Number.isNaN(Number.parseFloat(value.replace(",", ".")))
        }
      >
        <CheckIcon fontSize="small" />
      </Button>
    </Stack>
  );
};*/

const useMonthlyColumns = (
  year: string,
  avgPhmPrices: Record<string, Record<string, number>> | undefined,
  phmLiters: Record<string, Record<string, number>> | undefined,
  onSavePhmPrice: (year: string, month: string, price: number, liters?: number) => void
) =>
  useMemo<GridColDef<Income>[]>(
    () => [
      {
        field: 'label',
        headerName: 'Měsíc',
        flex: 1,
        type: 'string',
        filterable: true,
        headerClassName: 'theme--White',
        renderCell: (params) => {
          const { row } = params;
          return <Typography fontWeight="bold" component="span">{`${row.label}`}</Typography>;
        }
      },
      /*{
        field: 'avgPhmPrice',
        headerName: 'Průměrná cena PHM a litry',
        flex: 2,
        minWidth: 300,
        sortable: false,
        filterable: false,
        headerClassName: 'theme--White',
        renderCell: (params) => {
          const { row } = params;
          const paddedMonth = String(row.month).padStart(2, '0');
          const savedPrice = avgPhmPrices?.[year]?.[paddedMonth];
          const savedLiters = phmLiters?.[year]?.[paddedMonth];
          return (
            <PhmPriceCell
              row={row}
              year={year}
              savedPrice={savedPrice}
              savedLiters={savedLiters}
              onSave={onSavePhmPrice}
            />
          );
        }
      },*/
      {
        field: 'pdf',
        headerName: 'Výplata',
        flex: 1,
        type: 'string',
        filterable: false,
        headerClassName: 'theme--White',
        renderCell: (params) => {
          const { row } = params;
          if (!row.pdf) return;
          return (
            <Link href={row.pdf} underline="always" target="_blank">
              Stáhnout výplatní pásku
            </Link>
          );
        }
      }
    ],
    [year, avgPhmPrices, phmLiters, onSavePhmPrice]
  );

interface IncomesPageProps {
  incomes?: Income[];
  isLoadingData: boolean;
  currentYear?: Date;
  onYearChange?: (value: Date) => void;
}

const IncomesPageContent = ({ incomes, onYearChange, currentYear, isLoadingData }: IncomesPageProps) => {
  const { user } = useAuth();
  const { mutate: savePhmPrice } = useMutation(useCommonApi().setDealerAvgPhmPrice());

  const year = (currentYear ?? new Date()).getFullYear().toString();
  const monthRows = useMemo(() => incomes ?? [], [incomes]);

  const monthlyColumns = useMonthlyColumns(year, user?.avgPhmPrices, user?.phmLiters, (y, month, price, liters) => {
    savePhmPrice({ year: y, month, price, liters });
  });

  return (
    <PageLayout
      title="Výplaty"
      extra={
        <Stack direction="row" gap={2} sx={{ ...surfaceSx }} p={2}>
          <DatePicker
            label="Rok"
            format="yyyy"
            views={['year']}
            value={currentYear}
            onChange={(newDate) => newDate && onYearChange?.(newDate)}
            sx={{ width: 1 }}
          />
        </Stack>
      }
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          overflowX: 'auto',
          maxWidth: 1,
          width: 1,
          p: 1,
          '& .theme--White': {
            backgroundColor: '#fff'
          }
        }}
      >
        <DataGrid
          columns={monthlyColumns}
          rows={monthRows}
          loading={isLoadingData}
          sx={{
            width: '100%'
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          showCellVerticalBorder
          slotProps={{
            toolbar: { printOptions: { disableToolbarButton: true } }
          }}
          getRowId={(row: Income) => row.month}
        />
      </Box>
    </PageLayout>
  );
};

export const IncomesPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const { data: incomes, isPending } = useQuery(useCommonApi().getIncomes(currentDate.getFullYear().toString()));

  return (
    <IncomesPageContent
      incomes={incomes}
      isLoadingData={isPending}
      currentYear={currentDate}
      onYearChange={(value) => setCurrentDate(value)}
    />
  );
};
